import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { App } from "@capacitor/app";
import { AppState } from "../../store";
import { configureLiveUpdates, syncLiveUpdate, reloadApp, isNativePlatform } from "../../LiveUpdateService";
import LiveUpdateModal from "../../components/LiveUpdateModal";

interface LiveUpdateContainerProps {}

const LiveUpdateContainer: React.FC<LiveUpdateContainerProps> = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const initializingRef = useRef(false);
    const updatePendingRef = useRef(false);

    const checkForUpdate = useCallback(async () => {
        try {
            const updated = await syncLiveUpdate();
            if (updated) {
                updatePendingRef.current = true;
                setUpdateAvailable(true);
            }
        } catch (error) {
            console.error("[LiveUpdate] Failed to sync:", error);
        }
    }, []);

    const tryInit = useCallback(async () => {
        if (initializingRef.current || !navigator.onLine || !isNativePlatform()) {
            return;
        }
        initializingRef.current = true;

        try {
            await configureLiveUpdates();
            await checkForUpdate();
        } catch (error) {
            console.error("[LiveUpdate] Failed to initialize:", error);
            initializingRef.current = false;
        }
    }, [checkForUpdate]);

    useEffect(() => {
        tryInit();

        const handleOnline = () => {
            tryInit();
        };

        window.addEventListener("online", handleOnline);
        return () => {
            window.removeEventListener("online", handleOnline);
        };
    }, [tryInit]);

    useEffect(() => {
        if (!isNativePlatform()) {
            return;
        }

        const listener = App.addListener("resume", async () => {
            if (updatePendingRef.current) {
                await reloadApp();
            } else if (initializingRef.current) {
                await checkForUpdate();
            }
        });

        return () => {
            listener.then((handle) => handle.remove());
        };
    }, [checkForUpdate]);

    return <LiveUpdateModal isOpen={updateAvailable} onReload={reloadApp} />;
};

const mapStateToProps = (state: AppState) => {
    return {};
};

const mapDispatchToProps = (dispatch: any) => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(LiveUpdateContainer);
